import React, { CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBody, Row, Col, Button, Input, ModalProps } from 'reactstrap';
import { AlertMessage } from 'vl-common/src/components/AlertMessage/AlertMessage';
import { Api } from 'vl-common/src/api';

interface ChangePriorityModalProps extends ModalProps {
  prioritiesList: {
    urg_code: string;
    def_urg: number;
    urg_desc?: string;
    description?: string;
  }[];
  urgencyChangeReasons: {
    urgency_change_code: string;
    urgency_change_desc: string;
  }[];
  caseId: string;
  appointmentId?: number;
  orderLocation?: string;
  modalLocation: string;
  titleDarkSide?: string;
  noBtnHandler?: () => void;
  yesBtnHandler?: () => void;
  yesBtnText?: string;
  noBtnText?: string;
  toggle?: () => void;
}

const ChangePriorityModal = ({
  prioritiesList,
  urgencyChangeReasons,
  caseId,
  appointmentId,
  orderLocation,
  modalLocation = '',
  titleDarkSide = '',
  noBtnHandler,
  yesBtnHandler,
  yesBtnText = 'Confirm',
  noBtnText = 'Cancel',
  toggle,
  ...restProps
}: ChangePriorityModalProps) => {
  const { t } = useTranslation('common');
  const [selectedPriority, setSelectedPriority] = useState('');
  const [showAlertMessage, setShowAlertMessage] = useState({
    visibility: false,
    msg: ''
  });
  const [selectedReason, setSelectedReason] = useState('');
  const [modalContent, setModalContent] = useState({
    title: '',
    subText: '',
    prioritySubText: ''
  });

  const displayContent = () => {
    if (modalLocation === 'labs') {
      setModalContent({
        title: 'Change Randox test priority',
        subText:
          'Are you sure you want to change the urgency status of this Randox test? If yes, then please record a reason for this change',
        prioritySubText: 'Please select new Randox test priority'
      });
    }
    if (modalLocation === 'cases') {
      setModalContent({
        title: 'Change case priority',
        subText:
          'Are you sure you want to change the urgency status of this case? If yes, then please record a reason for this change',
        prioritySubText: 'Please select new case priority'
      });
    }
  };

  const displayAlertMessage = (msg = '') => {
    setShowAlertMessage({ visibility: true, msg });
    const t = setTimeout(() => {
      setShowAlertMessage({ visibility: false, msg });
      clearTimeout(t);
    }, 7000);
  };

  const onCancel = () => {
    setSelectedPriority('');
    setSelectedReason('');
    noBtnHandler();
  };

  const onToggle = () => {
    setSelectedPriority('');
    setSelectedReason('');
    toggle();
  };

  const onConfirm = () => {
    if (selectedReason && selectedPriority && modalLocation === 'cases') {
      Api.cases
        .changePriority(caseId, selectedPriority, selectedReason)
        .then(() => {
          yesBtnHandler();
        })
        .catch((err) => displayAlertMessage(err.message))
        .finally(() => {
          setSelectedPriority('');
          setSelectedReason('');
        });
    } else if (selectedReason && selectedPriority && modalLocation === 'labs') {
      Api.labs
        .changePriority(appointmentId, orderLocation, selectedPriority, selectedReason)
        .then(() => {
          yesBtnHandler();
        })
        .catch((err) => displayAlertMessage(err.message))
        .finally(() => {
          setSelectedPriority('');
          setSelectedReason('');
        });
    } else {
      displayAlertMessage('All inputs are required');
    }
  };

  return (
    <Modal
      size="lg"
      {...restProps}
      toggle={onToggle}
      centered
      data-testid="change-priority-modal"
      onOpened={displayContent}
    >
      <ModalBody>
        <Row>
          <Col className="vl-flex-col-center">
            <AlertMessage message={showAlertMessage.msg} visibility={showAlertMessage.visibility} />
            <div style={styles.title}>
              {modalContent.title}
              {titleDarkSide.trim().length > 0 ? <span style={styles.titleDarkSide}>{` ${titleDarkSide}`}</span> : null}
            </div>
            {modalContent.subText !== '' && <div style={styles.subText}>{modalContent.subText}</div>}
            <div style={styles.subText}>{modalContent.prioritySubText}</div>
            <div className="vl-select">
              <Input
                className="minimal"
                type="select"
                style={styles.select}
                value={selectedPriority}
                data-testid="case-priority"
                onChange={(e) => {
                  setSelectedPriority(e.target.value);
                }}
                placeholder={t('Select from dropdown')}
              >
                <option value={-1}>{t('Select from dropdown')}</option>
                {prioritiesList?.map(function ({ urg_code, urg_desc, description }) {
                  return (
                    <option key={urg_code} value={urg_code}>
                      {urg_desc || description}
                    </option>
                  );
                })}
              </Input>
            </div>
            <div style={styles.subText}>{t('Please select a reason')}</div>
            <div className="vl-select">
              <Input
                className="minimal"
                type="select"
                style={styles.select}
                value={selectedReason}
                data-testid="change-priority-reason"
                onChange={(e) => {
                  setSelectedReason(e.target.value);
                }}
                placeholder={t('Select from dropdown')}
              >
                <option value={-1}>{t('Select from dropdown')}</option>
                {urgencyChangeReasons?.map(function ({ urgency_change_code, urgency_change_desc }) {
                  return (
                    <option key={urgency_change_code} value={urgency_change_code}>
                      {urgency_change_desc}
                    </option>
                  );
                })}
              </Input>
            </div>
            <div className="vl-flex-row-center" style={styles.btnsContainer}>
              {noBtnText !== '' && (
                <Button
                  style={{ ...styles.btnGray }}
                  type="button"
                  onClick={onCancel}
                  data-testid="cancel-change-case-priority-modal-button"
                >
                  {noBtnText}
                </Button>
              )}
              {yesBtnText !== '' && (
                <Button
                  style={{ ...styles.btnOrang }}
                  type="button"
                  onClick={onConfirm}
                  data-testid="confirm-change-case-priority-button"
                >
                  {yesBtnText}
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
};

export { ChangePriorityModal };

const styles = {
  btnsContainer: { marginBottom: '30px' },
  btnOrang: {
    width: '110px',
    height: '40px',
    backgroundColor: 'var(--productPrimary)',
    fontSize: '14px',
    padding: '4px 10px',
    borderColor: 'var(--productPrimary)',
    color: 'var(--productPrimaryDark)'
  },
  btnGray: {
    width: '110px',
    height: '40px',
    backgroundColor: 'var(--productGray)',
    fontSize: '14px',
    padding: '4px 10px',
    borderColor: 'var(--productGray)',
    marginRight: '10px'
  },
  title: { color: 'var(--productPink)', fontSize: '30px', fontWeight: 'bold' },
  titleDarkSide: { color: 'var(--productPrimaryDark)', fontWeight: 'bold' },
  subText: {
    color: 'var(--productPrimaryDark)',
    fontSize: '14px',
    marginTop: '15px',
    marginBottom: '10px',
    textAlign: 'center'
  } as CSSProperties,
  select: {
    width: '400px',
    marginBottom: '30px'
  }
};
